import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Space,
  Popconfirm,
  message,
  Tag,
  Modal,
  Input,
} from "antd";
import { EditOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import { SearchOutlined } from "@ant-design/icons";
import Heading from "../../components/Heading";
import { zoneAPI } from "../../api/RateCardApi";
import AddZoneModal from "./AddZoneModal";
import { serialNum } from "../../utils";
import Highlighter from "react-highlight-words";

const ZoneManagement = () => {
  const [loading, setLoading] = useState(false);
  const [zoneList, setZoneList] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editSelectedZone, setEditSelectedZone] = useState({});
  const [pageSize, setPageSize] = useState(10);
  const [pageNum, setPageNum] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPageSize, setTotalPageSize] = useState(0);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedPincodes, setSelectedPincodes] = useState({});
  const [filters, setFilters] = useState({});
  const [debouncedFilters, setDebouncedFilters] = useState({});

  useEffect(() => {
    getAllZoneList({ pageNo: pageNum, pageSize });
  }, []);

  const getAllZoneList = async ({ pageNo, pageSize, debouncedFilters }) => {
    setLoading(true);
    const headers = {
      //parent: JSON.parse(localStorage.getItem("userinfo"))?.roles?.[0],
      parent: "HO",
    };
    const params = {
      page: pageNo,
      limit: pageSize,
      ...debouncedFilters,
    };

    let res = await zoneAPI.getAllZoneList(params, headers);
    if (res.status) {
      setZoneList(res?.response?.data?.data || []);
      setTotalPageSize(res?.response?.data?.total || 0);
      setLoading(false);
    } else {
      message.error(res?.response?.data?.message || "Internal Server Error");
      setLoading(false);
    }
    //});
  };

  useEffect(() => {
    if (debouncedFilters && Object.keys(debouncedFilters).length > 0) {
      const handler = setTimeout(() => {
        getAllZoneList({
          pageNo: pageNum,
          pageSize: pageSize,
          debouncedFilters,
        });
      }, 500);
      setPageNum(1);
      setCurrentPage(1);
      return () => clearTimeout(handler);
    }
  }, [debouncedFilters]);

  const handleTableChange = (pagination) => {
    getAllZoneList({
      pageNo: pagination?.current,
      pageSize: pagination.pageSize,
    });
    setPageSize(pagination.pageSize);
    setPageNum(pagination.current);
    setCurrentPage(pagination.current);
  };

  const itemRender = (_, type, originalElement) => {
    if (type === "prev") {
      return <a>Previous</a>;
    }
    if (type === "next") {
      return <a>Next</a>;
    }
    return (
      <div>
        <p>{originalElement} </p>
      </div>
    );
  };

  const handleEdit = async (record) => {
    setLoading(true);
    let res = await zoneAPI.getZoneById(record.id);
    if (res?.status) {
      let data = res?.response?.data?.data;
      console.log(data);

      setEditSelectedZone(data || {});
      setIsModalOpen(true);
      setLoading(false);
    } else {
      setLoading(false);
      message.error(res?.response?.data?.message || "Internal server error.");
    }

    // setEditSelectedZone(record);
    // setIsModalOpen(true);
  };

  const handleDelete = async (record) => {
    const response = await zoneAPI.deleteZone(record.id);
    if (response.status) {
      message.success("Zone deleted successfully");
      getAllZoneList({ pageNo: pageNum, pageSize });
    } else {
      message.error(
        response?.response?.data?.message || "Internal Server Error"
      );
    }
  };

  const handleViewAll = async (pincodes, record) => {
    setLoading(true);
    let res = await zoneAPI.getZoneById(record.id);
    if (res?.status) {
      let data = res?.response?.data?.data;

      setSelectedPincodes({
        zone: data?.name,
        pincodes: data?.pincodes || [],
      });
      setIsViewModalOpen(true);
      setLoading(false);
    } else {
      setLoading(false);
      message.error(res?.response?.data?.message || "Internal server error.");
    }
  };

  const handleSearch = (value, dataIndex) => {
    const updatedFilters = {
      ...filters,
      [dataIndex]: value?.toString(),
    };
    setFilters(updatedFilters);
    setDebouncedFilters(updatedFilters);
  };

  const getColumnSearchProps = (dataIndex, label = dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          allowClear
          placeholder={`Search ${label || dataIndex}`}
          value={selectedKeys[0] || ""}
          onChange={(e) => {
            if (dataIndex === "pincode") {
              const value = e.target.value ? [e.target.value] : [];
              setSelectedKeys(value);
              if (e.target.value?.length > 5 || e.target.value?.length === 0) {
                handleSearch(e.target.value, dataIndex);
              }
            } else {
              const value = e.target.value ? [e.target.value] : [];
              setSelectedKeys(value);
              handleSearch(e.target.value, dataIndex);
            }
          }}
        />
      </div>
    ),
    filterIcon: () => {
      return (
        <SearchOutlined
          style={{
            color: filters[dataIndex] ? "#1890ff" : undefined,
            fontSize: "16px",
          }}
        />
      );
    },

    render: (text) => {
      return filters[dataIndex] ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={[filters[dataIndex]]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text || "N/A"
      );
    },
  });

  const columns = [
    {
      title: "SI",
      dataIndex: "sl",
      width: "10%",
      render: (a, b, idx) => serialNum(pageSize, pageNum, idx),
    },
    {
      title: "Zone Name",
      dataIndex: "name",
      key: "name",
      width: "25%",
      align: "left",
      render: (text) => text || "N/A",
      ...getColumnSearchProps("searchTerm", "Zone Name"),
    },
    {
      title: "Pincodes",
      dataIndex: "pincodes",
      key: "pincodes",
      width: "45%",
      render: (pincodes, record) => (
        <Button type="link" onClick={() => handleViewAll(pincodes, record)}>
          View Pincodes <EyeOutlined />
        </Button>
      ),
      // ...getColumnSearchProps("pincode", "Pincode"),
      // render: (pincodes, record) => (
      //   <div
      //     style={{
      //       display: "flex",
      //       // flexWrap: "wrap",
      //       // gap: "8px",
      //       alignItems: "center",
      //     }}
      //   >
      //     {pincodes?.slice(0, 10).map((pincode, index) => (
      //       <Tag key={index} color="blue">
      //         {pincode}
      //       </Tag>
      //     ))}
      //     {pincodes?.length > 10 && (
      //       <Button
      //         type="link"
      //         onClick={() => handleViewAll(pincodes, record)}
      //         icon={<EyeOutlined />}
      //       >
      //         View All ({pincodes.length})
      //       </Button>
      //     )}
      //   </div>
      // ),
    },
    {
      title: "Action",
      key: "action",
      width: "10%",
      align: "center",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ color: "#1890ff" }}
          />
          <Popconfirm
            title="Are you sure you want to delete this zone?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              icon={<DeleteOutlined />}
              style={{ color: "#ff4d4f" }}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="master-main-container p-1">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "16px",
          alignItems: "center",
        }}
      >
        <Heading title="Zone Management" />
        <Button
          type="primary"
          className="save-btn"
          onClick={() => setIsModalOpen(true)}
        >
          Add Zone
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={zoneList}
        loading={loading}
        onChange={handleTableChange}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalPageSize,
          pageSizeOptions: ["10", "20", "50", "100"],
          showSizeChanger: true,
          itemRender: itemRender,
        }}
      />
      <AddZoneModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        editSelectedZone={editSelectedZone}
        setEditSelectedZone={setEditSelectedZone}
        zoneList={zoneList}
        getAllZoneList={() => getAllZoneList({ pageNo: pageNum, pageSize })}
      />

      <Modal
        title={`(${selectedPincodes?.zone}) Pincodes`}
        open={isViewModalOpen}
        onCancel={() => setIsViewModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsViewModalOpen(false)}>
            Close
          </Button>,
        ]}
        width={900}
      >
        <div className="pincodes-container">
          {selectedPincodes?.pincodes?.map((pincode, index) => (
            <Tag key={index} color="blue" style={{ margin: "4px" }}>
              {pincode}
            </Tag>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default ZoneManagement;
